import { ArrowLeft, Calendar } from 'lucide-react'
import { Button } from "@/components/ui/button"
import type { Artist } from "@/types/models"
import type { ChatUserInfo } from "@/lib/chatData"
import type { CachedChatDisplayData } from "@/lib/chatListCache"

interface ChatHeaderProps {
  isArtist: boolean
  artist: Artist | null
  chatUser: ChatUserInfo | null
  otherPartyLoading: boolean
  onBackClick: () => void
  getOtherPartyDisplayName: () => string
  getOtherPartySubtitle: () => string
  onArtistClick?: () => void
  onUserClick?: () => void
  showBookButton?: boolean
  onBookClick?: () => void
  bookButtonDisabled?: boolean
  cachedDisplayData?: CachedChatDisplayData | null
}

export function ChatHeader({
  isArtist,
  artist,
  chatUser,
  onBackClick,
  getOtherPartyDisplayName,
  getOtherPartySubtitle,
  onArtistClick,
  onUserClick,
  showBookButton = false,
  onBookClick,
  bookButtonDisabled = false,
  cachedDisplayData
}: ChatHeaderProps) {
  const renderAvatar = () => {
    // ✅ Always prefer cached avatar if available - it's the most reliable source
    // Even after loading completes, the cached data is accurate and persistent
    if (cachedDisplayData?.avatarUrl) {
      return (
        <img
          src={cachedDisplayData.avatarUrl}
          alt={cachedDisplayData.displayName}
          className="w-8 h-8 rounded-full object-cover flex-shrink-0"
        />
      )
    }

    // Fallback to cached initial if no URL
    if (cachedDisplayData && !cachedDisplayData.avatarUrl) {
      return (
        <div className="w-8 h-8 rounded-full bg-border flex items-center justify-center flex-shrink-0">
          <span className="text-foreground text-xs font-semibold">
            {cachedDisplayData.avatarInitial}
          </span>
        </div>
      )
    }

    if (isArtist) {
      // Artist perspective: show user's avatar if available
      if (chatUser?.avatarUrl) {
        return (
          <img
            src={chatUser.avatarUrl}
            alt={chatUser.displayName || chatUser.email}
            className="w-8 h-8 rounded-full object-cover flex-shrink-0"
          />
        )
      }
      // Fallback: initial on border tone (instagram preferred)
      const initialSource = chatUser?.instagram || chatUser?.email || chatUser?.displayName || '?'
      const initial = (initialSource.startsWith('@') ? initialSource.slice(1) : initialSource).charAt(0).toUpperCase()
      return (
        <div className="w-8 h-8 rounded-full bg-border flex items-center justify-center flex-shrink-0">
          <span className="text-foreground text-xs font-semibold">
            {initial}
          </span>
        </div>
      )
    }

    if (artist?.avatarUrl) {
      // User perspective: show artist avatar
      return (
        <img
          src={artist.avatarUrl}
          alt={artist.displayName}
          className="w-8 h-8 rounded-full object-cover flex-shrink-0"
        />
      )
    }

    // User perspective: fallback artist avatar — initial on border tone
    // Try displayName first, then firstName, then lastName, otherwise '?'
    const displaySource = artist?.displayName || artist?.firstName || artist?.lastName || '?'
    const artistInitial = displaySource.charAt(0).toUpperCase()
    return (
      <div className="w-8 h-8 rounded-full bg-border flex items-center justify-center flex-shrink-0">
        <span className="text-foreground text-xs font-semibold">
          {artistInitial}
        </span>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-3">
      <Button
        variant="ghost"
        size="icon"
        onClick={onBackClick}
        className="h-10 w-10 rounded-full flex-shrink-0"
      >
        <ArrowLeft className="h-5 w-5" />
      </Button>

      <button
        onClick={() => {
          console.log('[ChatHeader] Header button clicked:', { isArtist, hasUserClick: !!onUserClick, hasArtistClick: !!onArtistClick })
          if (isArtist && onUserClick) {
            console.log('[ChatHeader] Calling onUserClick')
            onUserClick()
          } else if (!isArtist && onArtistClick) {
            console.log('[ChatHeader] Calling onArtistClick')
            onArtistClick()
          }
        }}
        disabled={(isArtist && !onUserClick) || (!isArtist && !onArtistClick)}
        className={`flex items-center gap-3 min-w-0 flex-1 ${(isArtist && onUserClick) || (!isArtist && onArtistClick) ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
      >
        {renderAvatar()}

        <div className="min-w-0">
          <h1 className="text-base font-medium truncate">
            {getOtherPartyDisplayName()}
          </h1>
          <p className="text-xs text-muted-foreground">
            {getOtherPartySubtitle()}
          </p>
        </div>
      </button>

      {showBookButton && (
        <button
          onClick={onBookClick}
          disabled={bookButtonDisabled}
          className={`aspect-square flex items-center justify-center rounded-[var(--card-radius)] transition-all flex-shrink-0 ${
            bookButtonDisabled
              ? 'bg-muted text-muted-foreground cursor-not-allowed'
              : 'bg-primary hover:bg-primary/90 text-primary-foreground'
          }`}
          style={{ height: '44px' }}
        >
          <Calendar className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}